import React from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { formatDateTime } from 'helpers/formaters';
import { formatSize } from 'common/packages';

const ASSETS = [
  { key: 'file', label: 'Artifact', accept: undefined, action: 'Re-upload artifact' },
  { key: 'thumbnail', label: 'Thumbnail', accept: 'image/png', action: 'Replace (PNG)' },
  { key: 'icon', label: 'Icon', accept: 'image/png', action: 'Replace (PNG)' },
];

const AssetPanel = (props) => {
  const { verification, verifying, uploading, canUpload, onVerify, onReupload } = props;

  return (
    <Card>
      <CardHeader
        title="Stored objects"
        subheader="Verify reads S3 directly: it reports what is actually there, not what the database claims."
        action={
          onVerify ? (
            <Button onClick={onVerify} disabled={verifying}>
              {verifying ? 'Verifying…' : 'Verify'}
            </Button>
          ) : null
        }
      />
      <Divider />
      <CardContent>
        <Stack spacing={2}>
          {verification && verification.sizeMatchesDb === false && (
            <Alert severity="warning">
              The stored artifact's size does not match the size recorded in the
              database. Either the recorded size is wrong or the object in storage is
              not the artifact this package describes.
            </Alert>
          )}

          {verification && verification.icon && verification.icon.isDefault && (
            <Alert severity="info">
              This package has no icon of its own — the shared default icon is being
              served for it.
            </Alert>
          )}

          {!verification && !verifying && (
            <Typography variant="body2" color="text.secondary">
              Not verified yet. A package whose artifact never finished uploading looks
              perfectly normal until you check.
            </Typography>
          )}

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Asset</TableCell>
                <TableCell>Storage key</TableCell>
                <TableCell>Present</TableCell>
                <TableCell align="right">Size</TableCell>
                <TableCell>Last modified</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ASSETS.map((asset) => {
                const result = verification ? verification[asset.key] : null;
                const active = uploading && uploading.key === asset.key;
                return (
                  <TableRow key={asset.key}>
                    <TableCell>{asset.label}</TableCell>
                    <TableCell sx={{ wordBreak: 'break-all' }}>
                      {result ? result.key : '—'}
                    </TableCell>
                    <TableCell>
                      {result ? (
                        <Chip
                          size="small"
                          label={result.exists ? 'Yes' : 'Missing'}
                          color={result.exists ? 'success' : 'error'}
                          variant={result.exists ? 'filled' : 'outlined'}
                        />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {result && result.exists ? formatSize(result.size) : '—'}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {result && result.lastModified ? formatDateTime(result.lastModified) : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        component="label"
                        size="small"
                        disabled={!canUpload || Boolean(uploading)}
                      >
                        {asset.action}
                        <input
                          hidden
                          type="file"
                          accept={asset.accept}
                          onChange={(event) => {
                            const file = event.target.files[0];
                            event.target.value = '';
                            if (file) onReupload(asset.key, file);
                          }}
                        />
                      </Button>
                      {active && (
                        <Box sx={{ mt: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={uploading.progress || 0}
                          />
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Stack>
      </CardContent>
    </Card>
  );
};

AssetPanel.propTypes = {
  canUpload: PropTypes.bool,
  onReupload: PropTypes.func.isRequired,
  onVerify: PropTypes.func,
  uploading: PropTypes.object,
  verification: PropTypes.object,
  verifying: PropTypes.bool,
};

export default AssetPanel;
